<template>
  <v-card class="activation-receipt" elevation="3" max-width="500" mx-auto>
    <v-card-title class="receipt-header text-center justify-center">
      <div class="header-content">
        <h3 class="app-name">{{ ticketTitle }}</h3>
        <p class="receipt-title text-caption">
          {{ appName }}
          <span v-if="showSecondCopy" class="second-copy-badge">
            <v-chip x-small color="warning" text-color="white">2ª via</v-chip>
          </span>
        </p>
      </div>
    </v-card-title>

    <v-divider></v-divider>

    <v-card-text class="pa-4">
      <div class="transaction-section mb-4">
        <div class="info-row" v-if="activationInfo.transaction_id">
          <span class="label">Código da Transação:</span>
          <span class="value">{{ activationInfo.transaction_id }}</span>
        </div>

        <div class="info-row" v-if="activationInfo.product">
          <span class="label">Produto:</span>
          <span class="value">{{ activationInfo.product }}</span>
        </div>

        <div class="info-row" v-if="activationInfo.date">
          <span class="label">Data de Pagamento:</span>
          <span class="value">{{ formatDate(activationInfo.date) }}</span>
        </div>

        <div class="info-row" v-if="agentInfo">
          <span class="label">Agente:</span>
          <span class="value">{{ agentInfo.id }} - {{ agentInfo.name }}</span>
        </div>
      </div>

      <v-divider class="my-4"></v-divider>

      <div class="activation-section mb-4">
        <div class="highlighted-info" v-if="activationInfo.targetName">
          <v-icon left color="primary">mdi-account</v-icon>
          <span class="highlight-label">Responsável:</span>
          <span class="highlight-value">{{ activationInfo.targetName }}</span>
        </div>

        <div class="highlighted-info" v-if="activationInfo.plate">
          <v-icon left color="primary">mdi-car</v-icon>
          <span class="highlight-label">Placa:</span>
          <span class="highlight-value">{{ activationInfo.plate }}</span>
        </div>

        <div class="highlighted-info" v-if="activationInfo.vehicle_maker_model">
          <v-icon left color="primary">mdi-car-info</v-icon>
          <span class="highlight-label">Marca/Modelo:</span>
          <span class="highlight-value">{{ activationInfo.vehicle_maker_model }}</span>
        </div>

        <div class="time-info-grid">
          <v-card class="time-card pa-3" outlined>
            <div class="time-item">
              <v-icon color="success" class="mb-1">mdi-play-circle</v-icon>
              <div class="time-label">Início</div>
              <div class="time-value">{{ formatDate(activationInfo.date_time_begin) }}</div>
            </div>
          </v-card>

          <v-card class="time-card pa-3" outlined>
            <div class="time-item">
              <v-icon color="error" class="mb-1">mdi-stop-circle</v-icon>
              <div class="time-label">Validade</div>
              <div class="time-value">{{ formatDate(activationInfo.date_time_end) }}</div>
            </div>
          </v-card>
        </div>

        <div class="duration-info" v-if="activationInfo.time">
          <v-chip color="info" text-color="white" large>
            <v-icon left>mdi-clock</v-icon>
            {{ activationInfo.time }} minutos
          </v-chip>
        </div>
      </div>

      <v-divider class="my-4"></v-divider>

      <div class="financial-section mb-4">
        <h4 class="section-title">Detalhes Financeiros</h4>

        <div class="financial-row" v-if="activationInfo.price">
          <span class="financial-label">Valor:</span>
          <span class="financial-value">R$ {{ formatMoney(activationInfo.price) }}</span>
        </div>

        <div class="financial-row" v-if="activationInfo.tax">
          <span class="financial-label">Taxa:</span>
          <span class="financial-value">R$ {{ formatMoney(activationInfo.tax) }}</span>
        </div>

        <div class="financial-row" v-if="activationInfo.discount">
          <span class="financial-label discount">Desconto:</span>
          <span class="financial-value discount">- R$ {{ formatMoney(activationInfo.discount) }}</span>
        </div>

        <v-divider class="my-2"></v-divider>

        <div class="financial-row total" v-if="activationInfo.total_amount">
          <span class="financial-label">Valor Total:</span>
          <span class="financial-value">R$ {{ formatMoney(activationInfo.total_amount) }}</span>
        </div>

        <div class="payment-method" v-if="activationInfo.method">
          <v-chip color="success" text-color="white">
            <v-icon left>mdi-credit-card</v-icon>
            {{ activationInfo.method }}
          </v-chip>
        </div>
      </div>

      <v-divider class="my-4"></v-divider>

      <div class="company-section text-center">
        <div class="company-info">
          <p class="company-detail" v-if="companyInfo.cnpj">
            <strong>CNPJ:</strong> {{ formatCnpj(companyInfo.cnpj) }}
          </p>
          <p class="company-detail" v-if="companyInfo.endereco">{{ companyInfo.endereco }}</p>
          <p class="company-detail" v-if="companyInfo.cidade && companyInfo.uf">
            {{ companyInfo.cidade }}/{{ companyInfo.uf }}
          </p>
          <p class="company-detail" v-if="companyInfo.telefone_fixo">
            {{ formatPhone(companyInfo.telefone_fixo) }}
          </p>
        </div>
      </div>
    </v-card-text>

    <v-card-actions class="justify-center pa-4">
      <v-btn color="primary" outlined @click="printReceipt">
        <v-icon left>mdi-printer</v-icon>
        Imprimir
      </v-btn>
      <v-btn color="secondary" outlined @click="downloadPdf">
        <v-icon left>mdi-download</v-icon>
        Baixar PDF
      </v-btn>
    </v-card-actions>

    <div ref="thermalReceipt" class="thermal-receipt-template" style="display: none;">
      <div
          style="
          width: 7cm;
          padding: 8px;
          font-family: Arial !important;
          text-align: center;
          background: #eeeeee;
        "
      >
        <center>
          <div
              style="
              border-bottom: 1px dotted #000000;
              width: 6cm;
              padding-bottom: 8px;
              margin-bottom: 8px;
            "
          >
            <h4 style="margin: 0">{{ ticketTitle }}</h4>
            <p style="font-size: 12px; margin: 0">
              {{ activationInfo.title }}
              <span v-if="showSecondCopy">
                - 2ª via
              </span>
            </p>
          </div>
          <div
              style="
              border-bottom: 1px dotted #000000;
              width: 6cm;
              padding-bottom: 8px;
              margin-bottom: 8px;
            "
          >
            <p
                v-if="
                activationInfo.transaction_id !== null && activationInfo.transaction_id !== undefined
              "
                style="font-size: 12px; margin: 0"
            >
              Código da Transação: {{ activationInfo.transaction_id }}
            </p>
            <p
                v-if="activationInfo.product !== null && activationInfo.product !== undefined"
                style="font-size: 12px; margin: 0"
            >
              Produto: {{ activationInfo.product }}
            </p>
            <p
                v-if="activationInfo.date !== null && activationInfo.date !== null"
                style="font-size: 12px; margin: 0"
            >
              Data de Pagamento: {{ formatDate(activationInfo.date) }}
            </p>
            <p
                v-if="agentInfo !== null && agentInfo !== null"
                style="font-size: 12px; margin: 0"
            >
              Agente: {{ agentInfo.id }} - {{ agentInfo.name }}
            </p>
          </div>
          <div
              style="
              border-bottom: 1px dotted #000000;
              width: 6cm;
              padding-bottom: 8px;
              margin-bottom: 8px;
            "
          >
            <h2
                v-if="activationInfo.targetName !== null && activationInfo.targetName !== undefined"
                style="font-size: 14px; margin: 0"
            >
              Responsável: {{ activationInfo.targetName }}
            </h2>
            <h2
                v-if="activationInfo.plate !== null && activationInfo.plate !== undefined"
                style="font-size: 14px; margin: 0"
            >
              Placa: {{ activationInfo.plate }}
            </h2>
            <h2
                v-if="
                activationInfo.vehicle_maker_model !== null &&
                activationInfo.vehicle_maker_model !== undefined
              "
                style="font-size: 14px; margin: 0"
            >
              Marca/Modelo: {{ activationInfo.vehicle_maker_model }}
            </h2>
            <h2 style="font-size: 14px; margin: 0">
              Inicio: {{ formatDate(activationInfo.date_time_begin) }}
            </h2>
            <h2 style="font-size: 14px; margin: 0">
              Validade: {{ formatDate(activationInfo.date_time_end) }}
            </h2>
            <h2
                v-if="activationInfo.time !== null && activationInfo.time !== undefined"
                style="font-size: 14px; margin: 0"
            >
              Tempo: {{ activationInfo.time }} minutos
            </h2>
            <h2
                v-if="activationInfo.price !== null && activationInfo.price !== undefined"
                style="font-size: 14px; margin: 0"
            >
              Valor: R$ {{ formatMoney(activationInfo.price) }}
            </h2>
            <h2
                v-if="activationInfo.tax !== null && activationInfo.tax !== undefined"
                style="font-size: 14px; margin: 0"
            >
              Taxa: R$ {{ formatMoney(activationInfo.tax) }}
            </h2>
            <h2
                v-if="activationInfo.discount !== null && activationInfo.discount !== undefined"
                style="font-size: 14px; margin: 0"
            >
              Desconto: R$ {{ formatMoney(activationInfo.discount) }}
            </h2>
            <h2
                v-if="activationInfo.total_amount !== null && activationInfo.total_amount !== undefined"
                style="font-size: 14px; margin: 0"
            >
              Valor total: R$ {{ formatMoney(activationInfo.total_amount) }}
            </h2>
            <p
                v-if="activationInfo.method !== null && activationInfo.method !== undefined"
                style="font-size: 14px; margin: 0"
            >
              Forma: {{ activationInfo.method }}
            </p>
          </div>
          <div>
            <p style="font-size: 11px; margin: 0">
              CNPJ: {{ formatCnpj(companyInfo.cnpj) }}
            </p>
            <p style="font-size: 11px; margin: 0">{{ companyInfo.endereco }}</p>
            <p style="font-size: 11px; margin: 0">{{ companyInfo.cidade }}/{{ companyInfo.uf }}</p>
            <p style="font-size: 11px; margin: 0">{{ formatPhone(companyInfo.telefone_fixo) }}</p>
          </div>
        </center>
      </div>
    </div>
  </v-card>
</template>

<script>
export default {
  name: "ModernActivationReceipt",
  props: {
    info: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      agentInfo: null,
      companyInfo: {
        cnpj: null,
        endereco: null,
        cidade: null,
        telefone_fixo: null,
        uf: null
      }
    }
  },
  computed: {
    appName() {
      const appName = process.env.VUE_APP_NAME

      if (appName) {
        return appName
      }

      return ''
    },
    ticketTitle() {
      return 'COMPROVANTE DE ESTACIONAMENTO'
    },
    activationInfo() {
      return this.info || {}
    },
    showSecondCopy() {
      const segundaVia = this.activationInfo.segundavia

      return segundaVia !== null && segundaVia !== undefined && segundaVia !== false
    }
  },
  async mounted() {
    await this.loadCompanyInfo();
    this.printReceipt();
  },
  methods: {
    async loadCompanyInfo() {
      try {
        const response = await axios.get("/api/common/info/print")
        const data = response.data

        this.companyInfo.cnpj = data.identification
        this.companyInfo.endereco = data.address
        this.companyInfo.cidade = data.city
        this.companyInfo.telefone_fixo = data.contact
        this.companyInfo.uf = data.state
      } catch (error) {
        console.error('Erro ao carregar informações da empresa:', error)
      }
    },
    formatDate(timestamp) {
      if (!timestamp) {
        return ''
      }

      const date = new Date(timestamp * 1000)
      const options = {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      }

      return date.toLocaleDateString('pt-BR', options)
    },
    formatMoney(value) {
      if (!value) {
        return '0,00'
      }

      const numericValue = typeof value === 'string' ? parseFloat(value) : value
      const finalValue = numericValue / 100

      return finalValue.toFixed(2).replace('.', ',')
    },
    formatCnpj(cnpj) {
      if (!cnpj) {
        return ''
      }

      const cleanCnpj = cnpj.replace(/\D/g, '')

      return cleanCnpj.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5')
    },
    formatPhone(phone) {
      if (!phone) {
        return ''
      }

      const cleanPhone = phone.replace(/\D/g, '')

      if (cleanPhone.length === 11) {
        return cleanPhone.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3')
      }

      if (cleanPhone.length === 10) {
        return cleanPhone.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3')
      }

      return phone
    },
    printReceipt() {
      const thermalContent = this.$refs.thermalReceipt.innerHTML
      const printWindow = window.open('', '_blank')

      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="UTF-8">
          <title>Comprovante de Ativação</title>
          <style>
            @media print {
              body {
                margin: 0;
                padding: 10px;
              }
            }
            body {
              font-family: Arial, sans-serif;
              margin: 0;
              padding: 20px;
              display: flex;
              justify-content: center;
              background: #f5f5f5;
            }
          </style>
        </head>
        <body>
          ${thermalContent}
        </body>
        </html>
      `)

      printWindow.document.close()
      printWindow.focus()

      setTimeout(() => {
        printWindow.print()
        printWindow.close()
      }, 250)
    },
    downloadPdf() {
      const thermalContent = this.$refs.thermalReceipt.innerHTML

      const tempDiv = document.createElement('div')
      tempDiv.innerHTML = thermalContent
      tempDiv.style.position = 'fixed'
      tempDiv.style.left = '-9999px'
      tempDiv.style.top = '-9999px'

      document.body.appendChild(tempDiv)

      const htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="UTF-8">
          <title>Comprovante de Ativação</title>
          <style>
            @media print {
              body { margin: 0; padding: 10px; }
            }
            body {
              font-family: Arial, sans-serif;
              margin: 0;
              padding: 20px;
              display: flex;
              justify-content: center;
              background: #f5f5f5;
            }
          </style>
        </head>
        <body>
          ${thermalContent}
        </body>
        </html>
      `

      const blob = new Blob([htmlContent], { type: 'text/html' })
      const url = URL.createObjectURL(blob)

      const downloadLink = document.createElement('a')
      downloadLink.href = url
      downloadLink.download = `comprovante-ativacao-${this.activationInfo.transaction_id || Date.now()}.html`

      document.body.appendChild(downloadLink)
      downloadLink.click()

      document.body.removeChild(downloadLink)
      document.body.removeChild(tempDiv)
      URL.revokeObjectURL(url)
    }
  }
}
</script>

<style scoped>
.activation-receipt {
  font-family: 'Roboto', Arial, sans-serif !important;
}

.receipt-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 20px;
}

.header-content {
  text-align: center;
  width: 100%;
}

.app-name {
  margin: 0;
  font-weight: 300;
  font-size: 1.5rem;
}

.receipt-title {
  margin: 8px 0 0 0;
  opacity: 0.9;
}

.second-copy-badge {
  margin-left: 8px;
}

.transaction-section,
.activation-section,
.financial-section {
  margin-bottom: 24px;
}

.info-row {
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
  padding: 4px 0;
}

.label {
  color: #666;
  font-weight: 500;
}

.value {
  color: #333;
  font-weight: 400;
}

.highlighted-info {
  display: flex;
  align-items: center;
  margin-bottom: 12px;
  padding: 8px 0;
}

.highlight-label {
  color: #666;
  font-weight: 500;
  margin-right: 8px;
}

.highlight-value {
  color: #333;
  font-weight: 600;
}

.time-info-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin: 16px 0;
}

.time-card {
  text-align: center;
}

.time-item {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.time-label {
  font-size: 0.875rem;
  color: #666;
  font-weight: 500;
}

.time-value {
  font-size: 0.75rem;
  color: #333;
  font-weight: 600;
  margin-top: 4px;
}

.duration-info {
  text-align: center;
  margin-top: 16px;
}

.section-title {
  color: #333;
  font-weight: 500;
  margin-bottom: 16px;
  text-align: center;
}

.financial-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  padding: 4px 0;
}

.financial-label {
  color: #666;
  font-weight: 500;
}

.financial-value {
  color: #333;
  font-weight: 600;
}

.financial-row.total {
  font-size: 1.1rem;
  font-weight: bold;
  border-top: 2px solid #eee;
  padding-top: 12px;
  margin-top: 12px;
}

.financial-row.total .financial-value {
  color: #2196F3;
}

.discount .financial-label,
.discount .financial-value {
  color: #4CAF50;
}

.payment-method {
  text-align: center;
  margin-top: 16px;
}

.company-section {
  background-color: #f8f9fa;
  border-radius: 8px;
  padding: 16px;
}

.company-detail {
  margin: 4px 0;
  color: #666;
  font-size: 0.875rem;
}

.thermal-receipt-template {
  position: absolute !important;
  left: -9999px !important;
  top: -9999px !important;
  visibility: hidden !important;
}

@media print {
  .activation-receipt {
    box-shadow: none !important;
    max-width: none !important;
    margin: 0 !important;
  }

  .v-card-actions {
    display: none !important;
  }
}

@media (max-width: 600px) {
  .time-info-grid {
    grid-template-columns: 1fr;
  }

  .financial-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }

  .info-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }
}
</style>
